import './polyfills.tsx';

import * as Sentry from '@sentry/react';
import { initializeApiClient } from 'shared/api-client';
import { API } from 'shared/config';
import { initTokens } from 'shared/tokens';

import { clearStorage } from '@/lib/clearStorage.ts';
import { handleLazyLoadingErrors } from '@/lib/handleLazyLoadingErrors.ts';
import { logoutRedirect } from '@/services/new-utils.ts';

import reportWebVitals from './reportWebVitals.js';

const COMMIT_ID = (import.meta.env.VITE_COMMIT_ID?.slice(0, 8) || 'unknown') + '-ssg';
const APP_VERSION = import.meta.env.VITE_VERSION;
const APP_VERSION_FULL = `${APP_VERSION} (${COMMIT_ID})`;

(globalThis as any).APP_VERSION_FULL = APP_VERSION_FULL; // needed by lib/version.ts

initializeApiClient({
  canObtainNewTokens: (requestUrl, requestBody) => {
    // Legion flow starting request
    if (
      requestUrl === `${API.url}/brain/api/legion/` &&
      ['init', 'goal'].includes(requestBody?.content_type)
    ) {
      return true;
    }

    // Winners page
    if (requestUrl === `${API.url}/kv_store/api/data/campaign-winners/`) {
      return true;
    }

    return false;
  },
  onLogout: () => {
    clearStorage();
    logoutRedirect();
  },
});

if (!import.meta.env.SSR) {
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: 'https://c40dddd25d4117453729ff6ffff036db@o4510237939400704.ingest.de.sentry.io/4510237954015312',
      release: `${import.meta.env.VITE_NAME}@${APP_VERSION}+${COMMIT_ID}`,
      environment: import.meta.env.VITE_STAGE,
      allowUrls: [/https?:\/\/.*scoopr.no/],
      ignoreErrors: [
        /**
         * React internal error thrown when something outside react modifies the DOM
         * This is usually because of a browser extension or Chrome's built-in translate
         */
        "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
        "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
        // Web vitals
        "TypeError: Cannot read properties of undefined (reading 'domInteractive')",
        "TypeError: undefined is not an object (evaluating 'this._perf.domInteractive')",
        // Tiktok analytics
        "Can't find variable: ttq",
        'ttq is not defined',
      ],
      beforeSend: (event, hint) => {
        const stack = hint.syntheticException?.stack;

        if (
          stack?.includes('chrome-extension://') ||
          stack?.includes('i18n/pixel/static/main') ||
          stack?.includes('gtag/js')
        ) {
          return null;
        }

        return event;
      },
    });
  }

  handleLazyLoadingErrors();
  initTokens();

  console.log(`Portal version: ${APP_VERSION_FULL}`);

  reportWebVitals((metric: { name: string; value: number; rating?: string; id?: string }) => {
    if (!import.meta.env.PROD) {
      return;
    }
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: metric.name,
      level: 'info',
      data: {
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      },
    });
  });
}
